'use client';

import {
  IcTooltipArrowBlue,
  TooltipContentToBlueWrapper,
  TooltipResponsive,
  Typography,
} from '@musinsa/mds';
import { AnimatePresence, motion } from 'framer-motion';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getArrowStyle, hasReachedMaxCount, incrementTooltipVisibleCount } from './utils';

export interface TooltipProps {
  align:
    | 'top left'
    | 'top center'
    | 'top right'
    | 'bottom left'
    | 'bottom center'
    | 'bottom right'
    | 'left top'
    | 'left center'
    | 'left bottom'
    | 'right top'
    | 'right center'
    | 'right bottom';
  content: React.ReactNode;
  className?: string;
  /** 툴팁이 자동으로 닫히는 시간 (ms). undefined면 자동으로 닫히지 않음 */
  duration?: number;
  /** 툴팁이 닫힐 때 호출되는 콜백 */
  onCloseCallback?: () => void;
  /** localStorage 키. 설정 시 최대 횟수만큼만 노출되고 이후에는 표시되지 않음 */
  storageKey?: string;
  /** 툴팁을 표시할 최대 횟수 (기본값: 1) */
  maxCount?: number;
}

export function Tooltip({
  align,
  content,
  className,
  duration,
  onCloseCallback,
  storageKey,
  maxCount = 1,
}: TooltipProps) {
  // NOTE: 툴팁이 최대 표시 횟수에 도달했는지 LocalStorage에서 확인
  const shouldHide = storageKey && hasReachedMaxCount(storageKey, maxCount);
  const [isVisible, setIsVisible] = useState(!shouldHide);

  const onClose = useCallback(() => {
    // localStorage에 카운트 증가
    if (storageKey) {
      incrementTooltipVisibleCount(storageKey);
    }

    setIsVisible(false);
    onCloseCallback?.();
  }, [storageKey, onCloseCallback]);

  useEffect(() => {
    if (duration && duration > 0) {
      const removeTimer = setTimeout(() => {
        onClose();
      }, duration);

      return () => {
        clearTimeout(removeTimer);
      };
    }
  }, [duration, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <TooltipResponsive>
          <motion.div
            initial={{ opacity: 1, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            // exit={{ opacity: 0, scale: 0.8 }} # FIXME: scale 버그가 있어서 추후 개선필요
            exit={{ opacity: 0 }}
            transition={{
              scale: {
                type: 'spring',
                stiffness: 550,
                damping: 24,
              },
              opacity: {
                ease: 'linear',
                duration: 0.15,
              },
            }}
          >
            <TooltipContentToBlueWrapper className={className}>
              <div style={getArrowStyle(align)}>
                <IcTooltipArrowBlue />
              </div>
              <Typography
                colorScheme="white"
                lineClamp={10}
                variant="textEtc11pxReg"
                style={{ margin: 0, whiteSpace: 'nowrap' }}
              >
                {content}
              </Typography>
            </TooltipContentToBlueWrapper>
          </motion.div>
        </TooltipResponsive>
      )}
    </AnimatePresence>
  );
}
